import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ProjectHeader from "../_shared/ProjectHeader/ProjectHeader";
import { HeaderContext } from "../../../HeaderContext";
import {
  ANNUAL_OKR_LIVE_REVIEW,
  ANNUAL_OKR_REVIEW,
  ANNUAL_OKR_VIEW_OKRS,
} from "../../../data/RouteUrls";
import { ArrowRightIcon, AnnualOkrProgressBar } from "./AnnualOkrHelpers";
import useAnnualOkrStore, {
  useAnnualOkrStoreSelector,
} from "./_redux/AnnualOkrStore";
import { StandardTooltip } from "../../shared/StandardTooltip/StandardTooltip";
import AnnualOkrSteps from "./AnnualOkrSteps";
import AnnualOkrSkeleton from "./AnnualOkrSkeleton";
import "./AnnualOkr.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Annual OKR coach dashboard — one row per client, expandable to the eight-step
// timeline. The same accordion-card design as Strategy Canvas's and the
// Execution Blueprint's dashboards, deliberately: a coach moves between all
// three and they should behave identically.
//
// Live against `GET /coach/annual-okr/dashboard` (contract §6.1) as of
// 2026-09-21. The milestone timeline in each accordion is the server's
// `timestamps[]`, not a local list — see the note in AnnualOkrHelpers.js.
//
// ⚠️ This screen's existence reverses an earlier decision that the coach side
// would have no dashboard (D1 in docs/annual-okr/PROGRESS.md, superseded by D5).
// ─────────────────────────────────────────────────────────────────────────────

// Module scope, never inline — the selector hook resubscribes on `selector`
// identity change, so an inline arrow would resubscribe every render.
const selectDashboard = (state) => ({
  clients: state.clients,
  dashboardLoading: state.dashboardLoading,
});

const ClientCard = ({
  annualOkrId,
  stage,
  buttonEnabled = true,
  initials,
  name,
  ceo,
  filled,
  statusText,
  statusSubText,
  buttonText = "View OKRs",
  timestamps,
  isOpen,
  onToggle,
}) => {
  const history = useHistory();
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // ⚠️ TWO destinations, chosen by `stage`. EXW branches on a `destination`
  // field; §6.1 gives `stage` and a `button { label, enabled }` instead, so the
  // server decides what the CTA says and whether it works, and the FE decides
  // only where it goes.
  //
  // `LIVE_REVIEW_STARTED` goes STRAIGHT to the live review, not to the review
  // screen. The session is already running with the CEO in it, and the review
  // screen's Start Live Review button would be a second `start` on a run
  // already in LIVE_REVIEW_IN_PROGRESS — a 409 at best, and a coach reading
  // "start" while the meeting is underway at worst.
  //
  // `LOCKED` goes to the finished board. That reverses an earlier call which
  // sent it to the review screen on the grounds that its read-only state
  // handled it — true, but it answers the wrong question: once a run is locked
  // nobody wants the review that produced it, they want the OKRs.
  //
  // Everything else goes to the review screen.
  //
  // ⚠️ This pointed at a bare `/:annualOkrId` placeholder until 2026-09-21,
  // which is why "View Submitted Company OKRs" on a SENT_TO_COACH client landed
  // on an empty screen.
  //
  // `annual_okr_unique_id` is **undefined** on a client with no run yet, so the
  // button has nowhere to go — hence the guard rather than pushing a URL with
  // "undefined" in it.
  const canOpen = buttonEnabled && !!annualOkrId;

  const goToOkrs = (event) => {
    event.stopPropagation();
    if (!canOpen) return;
    const route =
      stage === "LIVE_REVIEW_STARTED"
        ? ANNUAL_OKR_LIVE_REVIEW
        : stage === "LOCKED"
          ? ANNUAL_OKR_VIEW_OKRS
          : ANNUAL_OKR_REVIEW;
    history.push(route.replace(":annualOkrId", annualOkrId));
  };

  // Measured rather than animated to `auto`: `max-height` cannot transition to
  // a keyword, so the open height is read off the content and set explicitly.
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    } else {
      setContentHeight(0);
    }
  }, [isOpen, filled]);

  return (
    <div className={`aok-card ${isOpen ? "open" : ""}`}>
      <div
        className="aok-card-row"
        onClick={onToggle}
        role="button"
        tabIndex={0}
      >
        <div className="aok-card-left">
          <div className="aok-avatar">{initials}</div>
          <div className="aok-info">
            <div className="aok-name">{name}</div>
            <div className="aok-ceo">{ceo}</div>
            <div className="aok-progress-row">
              {/* Fixed at eight segments now, not sized from the payload —
                  see the bar's own note. */}
              <AnnualOkrProgressBar filled={filled} />
              <div className="aok-status">
                <span className="aok-status-text">{statusText}</span>
                {statusSubText ? (
                  <>
                    <span className="aok-status-divider" />
                    <span className="aok-status-sub">{statusSubText}</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="aok-card-actions">
          {/* A disabled CTA has to say why rather than sitting there dead
              (CLAUDE.md). The tooltip needs a wrapper element because a
              disabled button swallows the mouse events OverlayTrigger
              listens for. */}
          {canOpen ? (
            <button className="aok-view-btn" onClick={goToOkrs}>
              {buttonText}
              <ArrowRightIcon />
            </button>
          ) : (
            <StandardTooltip tooltTipContent="This client hasn't started their Annual OKRs yet.">
              <span className="aok-view-btn-wrap">
                <button className="aok-view-btn" disabled>
                  {buttonText}
                  <ArrowRightIcon />
                </button>
              </span>
            </StandardTooltip>
          )}
        </div>
      </div>
      <div className="aok-accordion" style={{ maxHeight: contentHeight }}>
        <div ref={contentRef} className="aok-accordion-inner">
          <div className="aok-accordion-divider" />
          <AnnualOkrSteps filled={filled} timestamps={timestamps} />
        </div>
      </div>
    </div>
  );
};

// Keep in sync with the accordion transition duration in AnnualOkr.scss.
const ACCORDION_TRANSITION_MS = 350;

const AnnualOkr = () => {
  // Only one row open at a time — same reasoning as the two sibling
  // dashboards: close the current one first and only start opening the new one
  // once that collapse has finished, so the newly opened card doesn't appear to
  // jump upward as the one above it shrinks.
  const [openId, setOpenId] = useState(null);
  const openTimerRef = useRef(null);
  const HeaderMetaData = useContext(HeaderContext);

  const { clients, dashboardLoading } =
    useAnnualOkrStoreSelector(selectDashboard);
  // `clients === null` is "not fetched yet", distinct from `[]` = "fetched,
  // none" — so the skeleton shows on first paint rather than the empty state
  // flashing before the data lands.
  const loading = dashboardLoading || clients === null;

  useEffect(() => {
    document.title = "Art of Scale | Annual OKRs";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Annual OKRs" });
    useAnnualOkrStore.getState().fetchDashboard();
    // eslint-disable-next-line
  }, []);

  useEffect(() => () => clearTimeout(openTimerRef.current), []);

  const handleToggle = (id) => {
    clearTimeout(openTimerRef.current);

    if (openId === id) {
      setOpenId(null);
      return;
    }

    if (openId === null) {
      setOpenId(id);
      return;
    }

    setOpenId(null);
    openTimerRef.current = setTimeout(
      () => setOpenId(id),
      ACCORDION_TRANSITION_MS,
    );
  };

  return (
    <div>
      <ProjectHeader handleNavigation={() => {}} showBack={false} />
      <div id="annual-okr">
        <div className="aok-header">
          <div className="aok-title">Annual OKRs</div>
          <div className="aok-subtitle">
            {loading
              ? ""
              : `${clients.length} ${clients.length === 1 ? "client" : "clients"}`}
          </div>
        </div>
        {loading ? (
          <AnnualOkrSkeleton count={4} />
        ) : clients.length === 0 ? (
          <div className="aok-empty-state">
            No clients with Annual OKR access yet.
          </div>
        ) : (
          <div className="aok-list aok-fade-in">
            {clients.map((client) => (
              <ClientCard
                key={client.id}
                {...client}
                isOpen={openId === client.id}
                onToggle={() => handleToggle(client.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnualOkr;
